import { Nullable } from "../base-types";
import { ICultureLoader } from "../clients/culture-loader";
import { ICultureLoadOption } from "../contracts/querying/culture-load-option";
import { EntityLoadConfiguration, IEntityLoadConfiguration } from "../contracts/querying/entity-load-configuration";
import { LoadOption } from "../contracts/querying/load-options";
import { IPropertyLoadOption } from "../contracts/querying/property-load-option";
import { IQueryLoadConfiguration, QueryLoadConfiguration } from "../contracts/querying/query-load-configuration";
import { IRelationLoadOption } from "../contracts/querying/relation-load-option";
import CultureInfo from "../culture-info";
import Guard from "../guard";

export class LoadOptionsMapper {
    private static defaultCultures: Array<CultureInfo>;

    static toEntityLoadConfiguration(loadConfiguration?: IQueryLoadConfiguration): Nullable<EntityLoadConfiguration> {
        if (loadConfiguration == null) {
            return null;
        }

        return new EntityLoadConfiguration(
            loadConfiguration.cultureLoadOption?.deepClone() || undefined,
            loadConfiguration.propertyLoadOption?.deepClone() || undefined,
            loadConfiguration.relationLoadOption?.deepClone() || undefined
        );
    }

    static toQueryLoadConfiguration(loadConfiguration?: IEntityLoadConfiguration): Nullable<QueryLoadConfiguration> {
        if (loadConfiguration == null) {
            return null;
        }

        return new QueryLoadConfiguration({
            cultureLoadOption: loadConfiguration.cultureLoadOption?.deepClone() || undefined,
            propertyLoadOption: loadConfiguration.propertyLoadOption?.deepClone() || undefined,
            relationLoadOption: loadConfiguration.relationLoadOption?.deepClone() || undefined,
        });
    }

    static mapPropertyLoadOptions(options: IPropertyLoadOption): Nullable<Array<string>> {
        if (options == null) {
            return [];
        }

        let result: Nullable<Array<string>> = [];
        switch (options.loadOption) {
            case LoadOption.All:
                result = null;
                break;
            case LoadOption.Custom:
                if (options.properties != null) {
                    result = options.properties;
                }
                break;
        }
        return result;
    }

    static mapRelationLoadOptions(options: IRelationLoadOption): Nullable<Array<string>> {
        if (options == null) {
            return [];
        }

        let result: Nullable<Array<string>> = [];
        switch (options.loadOption) {
            case LoadOption.All:
                result = null;
                break;
            case LoadOption.Custom:
                if (options.relations != null) {
                    result = options.relations.map(relationSpecification => relationSpecification.name);
                }
                break;
        }
        return result;
    }

    static async mapCultureLoadOptionsAsync(
        options: ICultureLoadOption,
        loader: ICultureLoader
    ): Promise<Array<CultureInfo>> {
        if (options == null) {
            return [];
        }

        let result: Array<CultureInfo> = [];
        switch (options.loadOption) {
            case LoadOption.All:
                Guard.notNullOrUndefined(loader);

                result = await loader.getAllCulturesCachedAsync();
                break;
            case LoadOption.Default:
                Guard.notNullOrUndefined(loader);

                if (this.defaultCultures == null) {
                    const defaultCulture = await loader.getDefaultCultureCachedAsync();
                    this.defaultCultures = defaultCulture != null ? [defaultCulture] : [];
                }

                result = this.defaultCultures;
                break;
            case LoadOption.Custom:
                if (options.cultures != null) {
                    result = options.cultures;
                }
                break;
        }

        return result;
    }
}
